import React, { createContext, useState } from 'react';
import { GetDepositWallet, GetCashWallet, GetIncomeWallet, GetQuantumWallet } from '../../services/backendServices';
import { getCurrentDate, getDateBefore30Days } from '../../Util/helper_functions';
// Create a Context for the Wallet
export const WalletContext = createContext();
export const WalletProvider = ({ children }) => {
    // Here, we have used the following abbreviations for wallets:
    // DW - Deposit Wallet
    // CW - Cash Wallet
    // IW - Income Wallet
    // QW - Quantum Wallet

    const [includeDateRange, setIncludeDateRange] = useState(true); // Default to including date range OR NOT 
    // Current date to be used as default for wallet data
    const today = getCurrentDate();
    const thirtyDaysAgo = getDateBefore30Days(); // Get the date 30 days ago
    // State variables to store data for each wallet
    const [DWData, setDWData] = useState([]); // Deposit Wallet data
    const [CWData, setCWData] = useState([]); // Cash Wallet data
    const [IWData, setIWData] = useState([]); // Income Wallet data
    const [QWData, setQWData] = useState([]); // Quantum Wallet data


    // State variables for pagination (total pages for each wallet)
    const [DWTotalPages, setDWTotalPages] = useState(0);
    const [CWTotalPages, setCWTotalPages] = useState(0);
    const [IWTotalPages, setIWTotalPages] = useState(0);
    const [QWTotalPages, setQWTotalPages] = useState(0);

    // State variables for total records in each wallet
    const [DWTotalRecords, setDWTotalRecords] = useState(0);
    const [CWTotalRecords, setCWTotalRecords] = useState(0);
    const [IWTotalRecords, setIWTotalRecords] = useState(0);
    const [QWTotalRecords, setQWTotalRecords] = useState(0);

    // Payload parameters for each wallet (used in API requests)
    const [DWPayloadParams, setDWPayloadParams] = useState({ offset: 0, limit: 10, sort_by: 'create_time', order: 'DESC', start_date: thirtyDaysAgo, end_date: today });
    const [CWPayloadParams, setCWPayloadParams] = useState({ offset: 0, limit: 10, sort_by: 'create_time', order: 'DESC', start_date: today, end_date: today });
    const [IWPayloadParams, setIWPayloadParams] = useState({ offset: 0, limit: 10, sort_by: 'create_time', order: 'DESC', start_date: thirtyDaysAgo, end_date: today });
    const [QWPayloadParams, setQWPayloadParams] = useState({ offset: 0, limit: 10, sort_by: 'create_time', order: 'DESC', start_date: thirtyDaysAgo, end_date: today });

    // Function to fetch Deposit Wallet data based on current payload parameters
    const fetchDepositWalletData = async () => {
        try {
            const payloadParams = { ...DWPayloadParams }; // Clone payload params for modification
            if (payloadParams.start_date === today && payloadParams.end_date === today) delete payloadParams.end_date; // Remove end_date if same as start_date

            const res = await GetDepositWallet(payloadParams); // Call the API with modified parameters
            if (res) {
                setDWData(res.data); // Store data in state
                setDWTotalRecords(res.total_records || 0); // Set total records, default to 0 if undefined
                setDWTotalPages(Math.ceil(res.total_records / DWPayloadParams.limit)); // Calculate total pages
            } else {
                console.log('Invalid response structure for Deposit Wallet data:', res); // Log error if response is invalid
            }
        } catch (e) {
            console.error('Error fetching Deposit Wallet data:', e); // Catch and log any errors
        }
    };

    // Function to fetch Cash Wallet data based on current payload parameters
    const fetchCashWalletData = async () => {
        try {
            const payloadParams = { ...CWPayloadParams }; // Clone payload params for modification
            if (payloadParams.start_date === today && payloadParams.end_date === today) delete payloadParams.end_date;

            const res = await GetCashWallet(payloadParams); // Call the API with modified parameters
            if (res) {
                setCWData(res.data);
                setCWTotalRecords(res.total_records || 0);
                setCWTotalPages(Math.ceil(res.total_records / CWPayloadParams.limit));
            } else {
                console.log('Invalid response structure for Cash Wallet data:', res);
            }
        } catch (e) {
            console.error('Error fetching Cash Wallet data:', e);
        }
    };

    // Function to fetch Income Wallet data based on current payload parameters
    const fetchIncomeWalletData = async () => {
        try {
            const payloadParams = { ...IWPayloadParams };
            if (payloadParams.start_date === today && payloadParams.end_date === today) delete payloadParams.end_date;

            const res = await GetIncomeWallet(payloadParams);
            if (res) {
                setIWData(res.data);
                setIWTotalRecords(res.total_records || 0);
                setIWTotalPages(Math.ceil(res.total_records / IWPayloadParams.limit));
            } else {
                console.log('Invalid response structure for Income Wallet data:', res);
            }
        } catch (e) {
            console.error('Error fetching Income Wallet data:', e);
        }
    };
    // Function to fetch Quantum Wallet data based on current payload parameters
    const fetchQuantumWalletData = async () => {
        try {
            const payloadParams = { ...QWPayloadParams };
            if (payloadParams.start_date === today && payloadParams.end_date === today) delete payloadParams.end_date;

            const res = await GetQuantumWallet(payloadParams);
            if (res) {
                setQWData(res.data);
                setQWTotalRecords(res.total_records || 0);
                setQWTotalPages(Math.ceil(res.total_records / QWPayloadParams.limit));
            } else {
                console.log('Invalid response structure for Quantum Wallet data:', res);
            }
        } catch (e) {
            console.error('Error fetching Quantum Wallet data:', e);
        }
    };


    // ProvIse the state and functions to the context consumers
    return (
        <WalletContext.Provider
            value={{
                DWData, CWData, IWData, QWData, // Wallet data
                DWTotalPages, CWTotalPages, IWTotalPages, QWTotalPages, // Total pages for pagination
                DWTotalRecords, CWTotalRecords, IWTotalRecords, QWTotalRecords, // Total records
                DWPayloadParams, CWPayloadParams, IWPayloadParams, QWPayloadParams, // Current payload parameters
                fetchDepositWalletData, fetchCashWalletData, fetchIncomeWalletData, fetchQuantumWalletData, // Fetch functions
                setDWPayloadParams, setCWPayloadParams, setIWPayloadParams, setQWPayloadParams, // setpayloadparams
                includeDateRange, setIncludeDateRange
            }}
        >
            {children}
        </WalletContext.Provider>
    );
};
